import * as adminApi from '@mygpt/app';
const Http = adminApi.Http;
import i18next from 'i18next';
import { deepMerge } from '@/utils/helper';

// API基础路径
const OGS_API_BASE_URL = import.meta.env.VITE_OGS_API_BASE_URL;

// 定义接口类型
export interface Department {
  id: string;
  name: string;
  code: string;
  description: string;
  companyId: string | number;
  parentId: number | null;
  enabled: boolean;
  children?: Department[];
  userCount?: number;
  activedUserCount?: number; // 来自API的激活用户数
  allUserCount?: number; // 所有用户数字段
}

export interface User {
  id: string;
  uuid: string; // 用户唯一标识符
  name: string;
  email: string;
  actived: boolean;
  enabled: boolean;
  userCompanyEnabled: boolean;
  departmentId?: string;
  departmentName?: string;
  departmentPath?: string;
  roles?: Role[]; // 用户的所有角色
  roleName?: string;
  roleCode?: string;
  sysRoleId?: number;
  createdAt: string;
  updatedAt: string;
  authorityCodes?: string[];
}

export interface Role {
  id: number;
  code: string;
  name: string;
  enabled: boolean;
  description?: string;
}

export interface PermissionLevel {
  id: number;
  code: string;
  name: string;
  level: number;
}

// 用户导入相关类型定义
export interface ApiError {
  name: string;
  message: string;
  properties?: Record<string, any>;
}

export interface FieldError {
  name: string;
  error: ApiError;
  outputParameters?: Record<string, any>;
  errorEnum?: Record<string, any>;
}

export interface BaseApiResponse<T = any> {
  success: boolean;
  detailMessage?: string;
  outputParameters?: Record<string, any>;
  fieldErrors?: FieldError[];
  errorEnum?: Record<string, any>;
}

export interface UserImportTask {
  id: number;
  type: 'SIMPLE';
  fileName: string;
  charset: string;
  createrId: number;
  companyId: number;
  status: 'READY' | 'PROCESSING' | 'FINISHED';
  // 总数
  totalAmount: number;
  // 完成数
  finishedAmount: number;
  succeededAmount: number;
  failedAmount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImportErrorEntry {
  index: number;
  values: string;
  errorCode: {
    name: string;
    message: string;
  };
  detailMessage?: string;
}

export interface ImportedUser {
  id: number;
  uuid: string;
  name: string;
  email: string;
  actived: boolean;
  enabled: boolean;
  defaultCompanyId: number;
  userCompanyActived: boolean;
  userCompanyEnabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface UserImportSubmitResponse extends BaseApiResponse {
  id: string;
}

export interface UserImportResultResponse extends BaseApiResponse {
  userImport: UserImportTask;
}

export interface UserUpdateResultResponse extends BaseApiResponse {
  userUpdate: UserImportTask;
}

export interface UserImportDetailResponse extends BaseApiResponse {
  userImport: UserImportTask;
  errorEntries: ImportErrorEntry[];
  importedUsers: ImportedUser[];
}

// 导入任务分页查询——排序字段
export type UserImportOrderBy =
  | 'ID_DESC'
  | 'ID_ASC'
  | 'CREATED_AT_DESC'
  | 'CREATED_AT_ASC'
  | 'UPDATED_AT_DESC'
  | 'UPDATED_AT_ASC';

// 导入任务分页查询请求体
export interface UserImportSearchPageRequest {
  currentPage?: number;
  pageSize?: number;
  orderBys?: UserImportOrderBy[];
  minCreatedAt?: string;
  maxCreatedAt?: string;
  minUpdatedAt?: string;
  maxUpdatedAt?: string;
}

// 分页元信息（注意字段名 getpageSize 与后端保持一致）
export interface Paginator {
  currentPage: number;
  items: number;
  getpageSize: number;
  pages: number;
  startRow: number;
  endRow: number;
}

// 导入任务分页查询响应
export interface UserImportSearchPageResponse extends BaseApiResponse {
  paginator: Paginator;
  userImports: UserImportTask[];
}

// 权限等级映射
export const PERMISSION_LEVELS: Record<string, PermissionLevel> = {
  ADMIN: { id: 1, code: 'ADMIN', name: i18next.t('adminConsole.newConsole.roles.owner'), level: 0 },
  MANAGER: { id: 2, code: 'MANAGER', name: i18next.t('adminConsole.newConsole.roles.manager'), level: 1 },
  EDITOR: { id: 3, code: 'EDITOR', name: i18next.t('adminConsole.newConsole.roles.editor'), level: 2 },
  VIEWER: { id: 4, code: 'VIEWER', name: i18next.t('adminConsole.newConsole.roles.viewer'), level: 3 },
};

export class PermissionApiService {
  private companyId: string = '';
  private companyName: string = '';
  private companyInfoPromise: Promise<{ id: string; name: string } | null> | null = null;

  // 缓存数据
  private rolesCache: Role[] | null = null;
  private rolesCacheTime: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  // 正在进行的请求缓存，避免重复请求
  private pendingRequests = new Map<string, Promise<any>>();

  // 设置公司ID
  setCompanyId(companyId: string) {
    this.companyId = companyId;
  }

  // 获取当前公司信息（带缓存）
  async getCurrentCompanyInfo(): Promise<{ id: string; name: string } | null> {
    // 如果已经有公司信息，直接返回
    if (this.companyId && this.companyName) {
      return { id: this.companyId, name: this.companyName };
    }

    // 如果正在请求中，返回已有的promise避免重复请求
    if (this.companyInfoPromise) {
      return this.companyInfoPromise;
    }

    // 创建新的请求promise
    this.companyInfoPromise = this.fetchCompanyInfo();
    const result = await this.companyInfoPromise;
    this.companyInfoPromise = null; // 请求完成后清空
    return result;
  }

  // 实际获取公司信息的方法
  private async fetchCompanyInfo(): Promise<{ id: string; name: string } | null> {
    try {
      const response = await adminApi.getMyCompanyDefault();
      if (response?.data?.company) {
        const company = response.data.company;
        this.companyId = company.id || '';
        this.companyName = company.name || i18next.t('adminConsole.newConsole.common.defaultCompanyName');
        return {
          id: this.companyId,
          name: this.companyName,
        };
      }
    } catch (error) {
      console.error('获取默认公司失败:', error);
    }

    // 如果获取失败，尝试获取公司列表的第一个
    try {
      const response = await adminApi.getMyCompanies();
      if (response?.data?.companies?.length > 0) {
        const company = response.data.companies[0];
        this.companyId = company.id || '';
        this.companyName = company.name || i18next.t('adminConsole.newConsole.common.defaultCompanyName');
        return {
          id: this.companyId,
          name: this.companyName,
        };
      }
    } catch (error) {
      console.error('获取公司列表失败:', error);
    }

    return null;
  }

  // 获取当前公司ID
  async getCurrentCompanyId(): Promise<string> {
    if (this.companyId) {
      return this.companyId;
    }

    const companyInfo = await this.getCurrentCompanyInfo();
    if (companyInfo?.id) {
      return companyInfo.id;
    }

    throw new Error(i18next.t('adminConsole.newConsole.common.cannotGetCompanyInfo'));
  }

  // 获取组织架构
  async getOrganizationStructure(): Promise<Department[]> {
    const companyId = await this.getCurrentCompanyId();
    const response = await adminApi.getDepartmentByCompanyId(companyId);

    if (response?.departments) {
      // API已经返回了完整的树形结构，直接使用
      // 只需要确保ID都是字符串类型，并添加缺失的字段
      return this.processApiDepartments(response.departments);
    }

    return [];
  }

  // 处理API返回的部门数据（保持原有的树形结构）
  private processApiDepartments(departments: any[]): Department[] {
    return departments.map((dept) => this.processSingleDepartment(dept));
  }

  // 递归处理单个部门及其子部门
  private processSingleDepartment(dept: any): Department {
    return {
      ...dept,
      id: String(dept.id),
      parentId: dept.parentId,
      userCount: dept.activedUserCount || 0,
      activedUserCount: dept.activedUserCount || 0,
      allUserCount: dept.allUserCount, // 映射API返回的allUserCount字段
      children: dept.children ? dept.children.map((child: any) => this.processSingleDepartment(child)) : [],
    };
  }

  // 构建部门树
  private buildDepartmentTree(departments: any[]): Department[] {
    const departmentMap = new Map<string, Department>();
    const rootDepartments: Department[] = [];

    // 首先创建所有部门节点
    departments.forEach((dept) => {
      // 确保 id 转换为字符串
      const deptId = String(dept.id);
      departmentMap.set(deptId, {
        ...dept,
        id: deptId,
        children: [],
        userCount: dept.activedUserCount || 0, // 使用API返回的激活用户数
        activedUserCount: dept.activedUserCount || 0,
        allUserCount: dept.allUserCount, // 映射API返回的allUserCount字段
      });
    });

    // 构建树形结构
    departments.forEach((dept) => {
      const deptId = String(dept.id);
      const node = departmentMap.get(deptId);
      if (node) {
        // 检查是否有父部门，parentId 为 -1 表示顶级部门
        if (dept.parentId && dept.parentId !== -1 && dept.parentId !== '-1') {
          const parentId = String(dept.parentId);
          const parent = departmentMap.get(parentId);
          if (parent && parent.children) {
            parent.children.push(node);
          } else {
            // 如果找不到父部门，则作为根部门
            rootDepartments.push(node);
          }
        } else {
          // parentId 为 -1 或无效时，作为根部门
          rootDepartments.push(node);
        }
      }
    });

    return rootDepartments;
  }

  // 获取用户列表
  async getUserList(params?: {
    currentPage?: number;
    pageSize?: number;
    searchTerm?: string;
    departmentId?: string;
    enabled: boolean | null;
  }): Promise<{ users: User[]; total: number; departmentUsers: Record<string, string[]>; isolatedUsers: string[] }> {
    const companyId = await this.getCurrentCompanyId();

    // 并行获取所有需要的数据
    const [userResponse, deptUserResponse, rolesList] = await Promise.all([
      // 获取用户列表
      adminApi.getUserList({
        currentPage: params?.currentPage || 1,
        pageSize: params?.pageSize || 999999,
        companyId,
        inCompanyUserNameInfixLike: params?.searchTerm,
        enabled: params ? params.enabled : null,
      }),
      // 获取部门用户关系
      adminApi.getDepartmentUsersByCompanyId(companyId),
      // 获取所有角色信息（使用缓存）
      this.getRoles(),
    ]);

    const roleMap = new Map(rolesList.map((role) => [role.id, role]));

    // 获取所有角色的用户关系
    const allRoleCodes = rolesList.map((role) => role.code);
    let roleUserData = null;
    if (allRoleCodes.length > 0) {
      try {
        roleUserData = await adminApi.getAuthorityRoleUsers({ roleCodes: allRoleCodes });
      } catch (error) {
        console.error('获取角色用户关系失败:', error);
      }
    }

    const roleIdToUserIds = deepMerge(roleUserData?.roleIdToUserIds, roleUserData?.roleIdToDisabledUserIds) || {};

    // 合并用户信息
    const users =
      userResponse.users?.map((user) => {
        // 查找用户所属部门
        let departmentId = '';
        let departmentName = '';
        let departmentPath = '';

        if (deptUserResponse.departmentIdToUserIdsMap) {
          for (const [deptId, userIds] of Object.entries(deptUserResponse.departmentIdToUserIdsMap)) {
            if (userIds.includes(user.id)) {
              departmentId = deptId;
              // 这里需要根据部门ID查找部门名称
              break;
            }
          }
        }

        // 获取用户的所有角色
        const userRoles = rolesList.filter((role) => {
          const userIds = roleIdToUserIds[role.id] || [];
          return userIds.includes(user.id);
        });

        // 获取最高权限角色
        const highestRole = this.getUserHighestRole(userRoles);

        return {
          ...user,
          departmentId,
          departmentName,
          departmentPath,
          roles: userRoles, // 添加角色数组
          roleName: highestRole?.name || i18next.t('adminConsole.newConsole.roles.viewer'),
          roleCode: highestRole?.code || 'ROLE_VIEWER',
        };
      }) || [];

    return {
      users,
      total: users.length,
      departmentUsers: deptUserResponse.departmentIdToUserIdsMap || {},
      isolatedUsers: deptUserResponse.isolatedUserIds || [],
    };
  }

  // 获取用户的最高权限角色
  private getUserHighestRole(roles: Role[]): Role | null {
    if (!roles || roles.length === 0) {
      return null;
    }

    // 权限等级排序：ROLE_COMPANY_OWNER > ROLE_ADMIN > ROLE_ADMIN_DEPARTMENT > ROLE_EDITOR > ROLE_VIEWER
    const roleHierarchy: Record<string, number> = {
      ROLE_COMPANY_OWNER: 5,
      ROLE_ADMIN: 4,
      ROLE_ADMIN_DEPARTMENT: 3,
      ROLE_EDITOR: 2,
      ROLE_VIEWER: 1,
    };

    return roles.reduce((highest, current) => {
      if (!highest) return current;
      const currentLevel = roleHierarchy[current.code] || 0;
      const highestLevel = roleHierarchy[highest.code] || 0;
      return currentLevel > highestLevel ? current : highest;
    });
  }

  // 获取未分配部门的用户 - 已废弃，改为在 getUserList 中一并返回
  async getUnassignedUsers(): Promise<User[]> {
    // 该方法已废弃，未分配用户信息已包含在 getUserList 返回的 isolatedUsers 中
    console.warn('getUnassignedUsers 已废弃，请使用 getUserList 获取所有用户数据');
    const { users, isolatedUsers } = await this.getUserList();
    return users.filter((user) => isolatedUsers.includes(user.id));
  }

  // 创建用户
  async createUser(params: {
    name: string;
    email: string;
    password: string;
    departmentId?: string;
    sysRoleId?: number;
  }): Promise<any> {
    const companyId = await this.getCurrentCompanyId();

    // 创建用户，直接传递departmentId（保持字符串格式）
    const response = await adminApi.createUser({
      name: params.name,
      email: params.email,
      password: params.password,
      companyId,
      sysRoleId: params.sysRoleId,
      departmentId: params.departmentId,
    });

    console.log('permissionApi.createUser - 发送给API的数据:', {
      name: params.name,
      email: params.email,
      companyId,
      sysRoleId: params.sysRoleId,
      departmentId: params.departmentId,
    });

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.createUserFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 更新用户信息
  async updateUser(params: {
    id: string;
    name: string;
    email: string;
    enabled: boolean;
    sysRoleId?: number;
  }): Promise<any> {
    const response = await adminApi.updateUser(params);

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.updateUserFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 分配用户到部门
  async assignUserToDepartment(userId: string, departmentId: string): Promise<any> {
    const response = await adminApi.setUserDepartment({
      userIds: [userId],
      departmentId,
    });

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.assignUserFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 从部门移除用户
  async removeUserFromDepartment(userId: string, departmentId: string): Promise<any> {
    return adminApi.unsetUserDepartment({
      userIds: [userId],
      departmentId,
    });
  }

  // 批量分配用户到部门
  async batchAssignUsersToDepartment(userIds: string[], departmentId: string): Promise<any> {
    return adminApi.setUserDepartment({
      userIds,
      departmentId,
    });
  }

  // 启用/禁用用户
  async toggleUserStatus(userId: string, enabled: boolean, departmentId?: string): Promise<any> {
    const companyId = await this.getCurrentCompanyId();

    if (enabled) {
      return adminApi.setUserCompany({ userId, companyId, departmentId });
    } else {
      return adminApi.unsetUserCompany({ userId, companyId });
    }
  }

  // 重置用户密码
  async resetUserPassword(userId: string, newPassword: string): Promise<any> {
    return adminApi.resetUserPassword({
      id: userId,
      password: newPassword,
    });
  }

  // 创建部门
  async createDepartment(params: {
    name: string;
    code: string;
    description: string;
    parentId?: number | string;
  }): Promise<any> {
    const companyId = await this.getCurrentCompanyId();

    const response = await adminApi.createDepartment({
      name: params.name,
      code: params.code,
      description: params.description,
      parentId: params.parentId || -1,
      companyId: companyId || -1,
    });

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.createDepartmentFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 更新部门
  async updateDepartment(params: { id: string; name: string; code: string; description: string }): Promise<any> {
    const response = await adminApi.updateDepartment(params);

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.updateDepartmentFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 删除部门
  async deleteDepartment(departmentId: string): Promise<any> {
    const response = await adminApi.deleteDepartment({ id: departmentId });

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.deleteDepartmentFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 获取角色列表（带缓存）
  async getRoles(): Promise<Role[]> {
    // 检查缓存是否有效
    const now = Date.now();
    if (this.rolesCache && this.rolesCacheTime && now - this.rolesCacheTime < this.CACHE_DURATION) {
      return this.rolesCache;
    }

    // 检查是否有正在进行的请求
    const pendingKey = 'getRoles';
    if (this.pendingRequests.has(pendingKey)) {
      return this.pendingRequests.get(pendingKey);
    }

    // 创建新请求
    const promise = adminApi.getAuthorityRoles().then(
      (response) => {
        const roles = response.roles || [];
        // 更新缓存
        this.rolesCache = roles;
        this.rolesCacheTime = now;
        // 清除pending请求
        this.pendingRequests.delete(pendingKey);
        return roles;
      },
      (error) => {
        // 清除pending请求
        this.pendingRequests.delete(pendingKey);
        throw error;
      }
    );

    // 存储pending请求
    this.pendingRequests.set(pendingKey, promise);
    return promise;
  }

  // 获取可分配的角色列表（专门为用户新增和编辑使用）
  async getAssignableRoles(): Promise<Role[]> {
    try {
      const response = await adminApi.getAuthorityAssignableRoles();
      return response.roles || [];
    } catch (error: any) {
      console.error('获取可分配角色列表失败:', error);
      return [];
    }
  }

  // 清除缓存
  clearCache() {
    this.rolesCache = null;
    this.rolesCacheTime = 0;
    this.companyInfoPromise = null;
    this.companyId = '';
    this.companyName = '';
  }

  // 批量导入用户（使用新的简单导入接口）
  async batchImportUsers(
    users: Array<{
      name: string;
      email: string;
      password: string;
      departmentId?: string;
      sysRoleId?: number;
    }>
  ): Promise<any> {
    const companyId = await this.getCurrentCompanyId();
    const results = [];

    for (const user of users) {
      try {
        const response = await this.createUser(user);
        results.push({ success: true, user: response.data?.user, error: null });
      } catch (error: any) {
        results.push({ success: false, user: null, error: error.message });
      }
    }

    return results;
  }

  // 简单批量导入用户（使用 CSV/Excel 文件）
  async simpleImportUsers(file: File): Promise<any> {
    const { post } = Http.use();
    const formData = new FormData();
    formData.append('importFile', file);

    try {
      // 不要手动设置 Content-Type，让浏览器自动设置包含 boundary
      const response = await post(`${OGS_API_BASE_URL}/user/import/simple`, formData);
      return response.data;
    } catch (error: any) {
      console.error('Import User Error:', error);
      throw error;
    }
  }

  // 简单批量更新用户（使用 CSV/Excel 文件）- 后端异步处理
  async asyncSimpleUpdateUsers(file: File): Promise<UserImportSubmitResponse> {
    const { post } = Http.use();
    const formData = new FormData();
    formData.append('updateFile', file);

    try {
      // 异步导入提交接口
      const response = await post(`${OGS_API_BASE_URL}/user/update/simple/async/submit`, formData);
      return response.data;
    } catch (error: any) {
      console.error('Async Update User Error:', error);
      throw error;
    }
  }

  // 查看异步更新结果
  async getAsyncSimpleUpdateUsersResult(taskID: string): Promise<UserUpdateResultResponse> {
    const { get } = Http.use();

    try {
      const response = await get(`${OGS_API_BASE_URL}/user/update/simple/async/${taskID}/result`);
      return response.data;
    } catch (error: any) {
      console.error('Fetch Async Update Result Error:', error);
      throw error;
    }
  }

  // 查看异步更新的详情（成功详情、失败的详情）
  async getAsyncSimpleUpdateUsersResultDetail(taskID: string): Promise<UserImportDetailResponse> {
    const { get } = Http.use();

    try {
      const response = await get(`${OGS_API_BASE_URL}/user/update/simple/async/${taskID}/result/detail`);
      return response.data;
    } catch (error: any) {
      console.error('Fetch Async Update Result Error:', error);
      throw error;
    }
  }

  // 简单批量导入用户（使用 CSV/Excel 文件）- 异步导入提交
  async asyncSimpleImportUsers(file: File): Promise<UserImportSubmitResponse> {
    const { post } = Http.use();
    const formData = new FormData();
    formData.append('importFile', file);

    try {
      // 异步导入提交接口
      const response = await post(`${OGS_API_BASE_URL}/user/import/simple/async/submit`, formData);
      return response.data;
    } catch (error: any) {
      console.error('Async Import User Error:', error);
      throw error;
    }
  }

  // 查看异步导入结果
  async getAsyncSimpleImportUsersResult(taskID: string): Promise<UserImportResultResponse> {
    const { get } = Http.use();

    try {
      const response = await get(`${OGS_API_BASE_URL}/user/import/simple/async/${taskID}/result`);
      return response.data;
    } catch (error: any) {
      console.error('Fetch Async Import Result Error:', error);
      throw error;
    }
  }

  // 查看异步导入的详情（成功详情、失败的详情）
  async getAsyncSimpleImportUsersResultDetail(taskID: string): Promise<UserImportDetailResponse> {
    const { get } = Http.use();

    try {
      const response = await get(`${OGS_API_BASE_URL}/user/import/simple/async/${taskID}/result/detail`);
      return response.data;
    } catch (error: any) {
      console.error('Fetch Async Import Result Error:', error);
      throw error;
    }
  }

  // 分页查询当前公司所有用户导入任务
  async searchImportTasks(body: UserImportSearchPageRequest = {}): Promise<UserImportSearchPageResponse> {
    const { post } = Http.use();
    const payload: UserImportSearchPageRequest = {
      currentPage: 1,
      pageSize: 10,
      orderBys: ['CREATED_AT_DESC'],
      ...body,
    };

    try {
      const response = await post(`${OGS_API_BASE_URL}/user/import/simple/async/search/page`, payload);
      return response.data;
    } catch (error: any) {
      console.error('Search Import Tasks Error:', error);
      throw error;
    }
  }

  // 生成随机密码
  generatePassword(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  }

  // 获取用户的API密钥列表
  async getUserApiKeys(userUuid: string): Promise<any[]> {
    try {
      const response = await adminApi.adminGetUserApiKeys(userUuid);
      return response?.data || [];
    } catch (error) {
      console.error('获取API密钥失败:', error);
      return [];
    }
  }

  // 为用户添加API密钥
  async addUserApiKey(userUuid: string, apiKey: string, description?: string): Promise<boolean> {
    try {
      await adminApi.adminCreateUserApiKey({ userId: userUuid, apiKey, description });
      return true;
    } catch (error) {
      console.error('添加API密钥失败:', error);
      return false;
    }
  }

  // 删除用户的API密钥
  async deleteUserApiKey(userUuid: string, apiKey: string): Promise<boolean> {
    try {
      await adminApi.adminDeleteUserApiKey({ userId: userUuid, apiKey });
      return true;
    } catch (error) {
      console.error('删除API密钥失败:', error);
      return false;
    }
  }

  // 同时更新用户部门和角色
  async updateUserDepartmentAndRole(params: {
    userIds: string[];
    departmentId: string;
    sysRoleId: string;
  }): Promise<any> {
    const response = await adminApi.updateUserDepartmentAndRole(params);

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.updateUserDeptRoleFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }

  // 批量删除用户
  async batchDeleteUsers(userIds: string[]): Promise<any> {
    const isCloudMode = import.meta.env.VITE_MODE !== 'local';

    let response;
    if (isCloudMode && this.companyId) {
      // Cloud版：删除用户与公司的关联
      response = await adminApi.deleteUsersCompany({
        userIds,
      });
    } else {
      // 本地部署版：物理删除用户
      response = await adminApi.batchDeleteUsers({ ids: userIds });
    }

    // 检查响应是否成功
    if (response.data && response.data.success === false) {
      // 创建包含 name 属性的错误对象，用于国际化
      const error: any = new Error(
        response.data.error?.message || i18next.t('adminConsole.newConsole.errors.batchDeleteUsersFailed')
      );
      error.name = response.data.error?.name;
      throw error;
    }

    return response;
  }
}

// 导出单例
export const permissionApi = new PermissionApiService();
